import React from 'react';
import FileIconSVG from '../FileIconSVG';

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string | null;
  documentName: string;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  isOpen,
  onClose,
  documentUrl,
  documentName,
}) => {
  if (!isOpen || !documentUrl) return null;

  const getFileExtension = (fileName: string, url: string | null): string => {
    // 1. Intentar desde el nombre
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      const ext = parts[parts.length - 1].toLowerCase();
      if (ext) return ext;
    }
    
    // 2. Intentar desde la URL
    if (url) {
      try {
        const urlPath = url.split('?')[0];
        const parts = urlPath.split('.');
        const ext = parts[parts.length - 1].toLowerCase();
        if (ext && ext.length <= 5) return ext;
      } catch (e) {
        // Ignorar
      }
    }
    
    return '';
  };

  const getViewerUrl = (url: string, fileName: string): string => {
    const extension = getFileExtension(fileName, url);
    
    // Para PDFs, usar la URL directamente en el iframe
    if (extension === 'pdf') {
      return url;
    }
    
    // Para documentos de Office, usar Office Online Viewer en modo EMBED
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    
    // Para otros formatos, intentar mostrar directamente
    return url;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 9999
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
              <FileIconSVG 
                filename={documentName} 
                extension={getFileExtension(documentName, documentUrl)}
                size={20} 
                className="flex-shrink-0" 
              />
              <h3
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {documentName}
              </h3>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Close (ESC)"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Document Viewer */}
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f9fafb'
            }}
          >
            <iframe
              src={getViewerUrl(documentUrl, documentName)}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={documentName}
            />
          </div>
        </div>
      </div>

      {/* ESC key handler */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default DocumentViewerModal;







